import React from 'react';

class StatsDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: 'connecting', // 'connecting', 'connected', 'error'
      errorMessage: '',
      stats: null,
      logs: [],
      totalLogs: 0,
      sessions: [],
      currentSessionIndex: 0,
      logsLoading: false,
      logsLoaded: false
    };
    this.dashboardRef = React.createRef();
    this.resizeObserver = null;
  }

  componentDidMount() {
    const { socket } = this.props;

    // Setup iframe height communication
    this.setupIframeResize();

    socket.on('statsConnected', (stats) => {
      console.log('Stats connected:', stats);
      this.setState({
        status: 'connected',
        stats: stats
      });
    });

    socket.on('statsError', (error) => {
      console.log('Stats error:', error);
      this.setState({
        status: 'error',
        errorMessage: error.message || 'Connection failed'
      });
    });

    socket.on('statsUpdate', (stats) => {
      console.log('Stats update:', stats);
      this.setState({ stats: stats });
    });

    socket.on('logsData', (data) => {
      console.log('Logs received:', data);
      const sessions = this.parseLogsIntoSessions(data.logs || []);
      this.setState({
        logs: data.logs || [],
        totalLogs: data.totalLogs || 0,
        sessions: sessions,
        currentSessionIndex: sessions.length > 0 ? sessions.length - 1 : 0,
        logsLoading: false,
        logsLoaded: true
      });
    });

    socket.on('disconnect', () => {
      this.setState({
        status: 'error',
        errorMessage: 'Disconnected from server'
      });
    });
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  componentDidUpdate() {
    // Send height after each update in case content changed
    this.sendHeightToParent();

    // Setup ResizeObserver if not already done and ref is available
    if (!this.resizeObserver && this.dashboardRef.current && window.self !== window.top) {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.sendHeightToParent();
        });
        this.resizeObserver.observe(this.dashboardRef.current);
      }
    }
  }

  setupIframeResize() {
    // Only setup if we're in an iframe
    if (window.self === window.top) return;

    // Initial height send after a short delay to ensure DOM is ready
    setTimeout(() => this.sendHeightToParent(), 100);
  }

  sendHeightToParent() {
    // Only send if we're in an iframe
    if (window.self === window.top) return;

    const height = document.documentElement.scrollHeight;
    window.parent.postMessage({
      type: 'statsIframeResize',
      height: height
    }, '*');
  }

  parseLogsIntoSessions(logs) {
    const sessions = [];
    let currentSession = null;
    const activeSocketIds = new Set();

    for (const log of logs) {
      if (log.eventType === 'CONNECTION') {
        const socketId = log.data?.socketId;

        // If no active connections, start a new session
        if (activeSocketIds.size === 0) {
          // Save previous session if it has events
          if (currentSession && currentSession.events.length > 0) {
            sessions.push(currentSession);
          }
          // Start a new session
          currentSession = {
            startTime: log.timestamp,
            endTime: null,
            events: []
          };
        }

        if (socketId) {
          activeSocketIds.add(socketId);
        }

        if (currentSession) {
          currentSession.events.push(log);
          currentSession.endTime = log.timestamp;
        }
      } else if (log.eventType === 'DISCONNECTION') {
        const socketId = log.data?.socketId;

        if (socketId) {
          activeSocketIds.delete(socketId);
        }

        if (currentSession) {
          currentSession.events.push(log);
          currentSession.endTime = log.timestamp;
        }
      } else {
        // Other events (COUPLE_START, COUPLE_END)
        if (currentSession) {
          currentSession.events.push(log);
          currentSession.endTime = log.timestamp;
        }
      }
    }

    // Don't forget to save the last session
    if (currentSession && currentSession.events.length > 0) {
      sessions.push(currentSession);
    }

    return sessions;
  }

  loadLogs = () => {
    const { socket } = this.props;
    this.setState({ logsLoading: true });
    socket.emit('requestLogs');
  };

  goToSession = (index) => {
    this.setState({ currentSessionIndex: index });
  };

  formatDate(isoString) {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  }

  getEventTypeClass(eventType) {
    switch (eventType) {
      case 'CONNECTION':
        return 'event-connection';
      case 'DISCONNECTION':
        return 'event-disconnection';
      case 'COUPLE_START':
        return 'event-couple-start';
      case 'COUPLE_END':
        return 'event-couple-end';
      default:
        return '';
    }
  }

  renderError() {
    return (
      <div className="stats-dashboard" ref={this.dashboardRef}>
        <div className="stats-error">
          <h2>Connection Error</h2>
          <p>{this.state.errorMessage}</p>
          <p>Please check your admin token and try again.</p>
        </div>
      </div>
    );
  }

  renderLoading() {
    return (
      <div className="stats-dashboard" ref={this.dashboardRef}>
        <div className="stats-loading">
          <div className="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p>Connecting to stats...</p>
        </div>
      </div>
    );
  }

  renderSummaryCards() {
    const { stats } = this.state;
    if (!stats) return null;

    const { summary } = stats;

    return (
      <div className="stats-cards">
        <div className="stats-card">
          <div className="stats-card-value">{summary.connectedUsers}</div>
          <div className="stats-card-label">Connected Users</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-value">{summary.activeCouples}</div>
          <div className="stats-card-label">Active Couples</div>
        </div>
        <div className="stats-card">
          <div className="stats-card-value">{summary.orphanUsers}</div>
          <div className="stats-card-label">Waiting Users</div>
        </div>
      </div>
    );
  }

  renderConnectedUsersTable() {
    const { stats } = this.state;
    if (!stats || !stats.connectedUsers) return null;

    return (
      <div className="stats-section">
        <h3>Connected Users ({stats.connectedUsers.length})</h3>
        {stats.connectedUsers.length === 0 ? (
          <p className="stats-empty">No users connected</p>
        ) : (
          <table className="stats-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Socket ID</th>
                <th>Ready</th>
                <th>In Couple</th>
              </tr>
            </thead>
            <tbody>
              {stats.connectedUsers.map((user) => (
                <tr key={user.socketId}>
                  <td>{user.name}</td>
                  <td className="socket-id">{user.socketId}</td>
                  <td>{user.ready ? 'Yes' : 'No'}</td>
                  <td>{user.inCouple ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  renderActiveCouplesTable() {
    const { stats } = this.state;
    if (!stats || !stats.activeCouples) return null;

    return (
      <div className="stats-section">
        <h3>Active Couples ({stats.activeCouples.length})</h3>
        {stats.activeCouples.length === 0 ? (
          <p className="stats-empty">No active couples</p>
        ) : (
          <table className="stats-table">
            <thead>
              <tr>
                <th>User 1</th>
                <th>User 2</th>
              </tr>
            </thead>
            <tbody>
              {stats.activeCouples.map((couple, index) => (
                <tr key={index}>
                  <td>{couple.user1.name} <span className="socket-id-small">({couple.user1.socketId})</span></td>
                  <td>{couple.user2.name} <span className="socket-id-small">({couple.user2.socketId})</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  renderOrphanUsersTable() {
    const { stats } = this.state;
    if (!stats || !stats.orphanUsers) return null;

    return (
      <div className="stats-section">
        <h3>Waiting Users ({stats.orphanUsers.length})</h3>
        {stats.orphanUsers.length === 0 ? (
          <p className="stats-empty">No users waiting</p>
        ) : (
          <table className="stats-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Socket ID</th>
                <th>Ready</th>
              </tr>
            </thead>
            <tbody>
              {stats.orphanUsers.map((user) => (
                <tr key={user.socketId}>
                  <td>{user.name}</td>
                  <td className="socket-id">{user.socketId}</td>
                  <td>{user.ready ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  renderSessionInfo() {
    const { stats } = this.state;
    if (!stats || !stats.session) return null;

    const { session } = stats;

    return (
      <div className="stats-section">
        <h3>Current Session</h3>
        <div className="session-info">
          <div className="session-info-row">
            <span className="session-label">Session ID:</span>
            <span className="session-value">{session.id || 'No active session'}</span>
          </div>
          <div className="session-info-row">
            <span className="session-label">Started:</span>
            <span className="session-value">{this.formatDate(session.startTime)}</span>
          </div>
          <div className="session-info-row">
            <span className="session-label">Status:</span>
            <span className={`session-status ${session.active ? 'active' : 'inactive'}`}>
              {session.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {session.disconnectedUsers && session.disconnectedUsers.length > 0 && (
          <div className="disconnected-users">
            <h4>Disconnected Users (this session)</h4>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Socket ID</th>
                  <th>Disconnected At</th>
                </tr>
              </thead>
              <tbody>
                {session.disconnectedUsers.map((user, index) => (
                  <tr key={index}>
                    <td>{user.name}</td>
                    <td className="socket-id">{user.socketId}</td>
                    <td>{this.formatDate(user.disconnectedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  renderLogsSection() {
    const { logsLoaded, logsLoading, sessions, currentSessionIndex } = this.state;

    return (
      <div className="stats-section logs-section">
        <div className="logs-header">
          <h3>Logs</h3>
          <button
            className="btn load-logs-btn"
            onClick={this.loadLogs}
            disabled={logsLoading}
          >
            {logsLoading ? 'Loading...' : (logsLoaded ? 'Refresh Logs' : 'Load Logs')}
          </button>
        </div>

        {logsLoaded && sessions.length === 0 && (
          <p className="stats-empty">No logs available</p>
        )}

        {logsLoaded && sessions.length > 0 && (
          <>
            <div className="session-pagination">
              <button
                className="btn pagination-btn"
                disabled={currentSessionIndex === 0}
                onClick={() => this.goToSession(0)}
              >
                First
              </button>
              <button
                className="btn pagination-btn"
                disabled={currentSessionIndex === 0}
                onClick={() => this.goToSession(currentSessionIndex - 1)}
              >
                Previous
              </button>
              <span className="session-indicator">
                Session {currentSessionIndex + 1} of {sessions.length}
              </span>
              <button
                className="btn pagination-btn"
                disabled={currentSessionIndex === sessions.length - 1}
                onClick={() => this.goToSession(currentSessionIndex + 1)}
              >
                Next
              </button>
              <button
                className="btn pagination-btn"
                disabled={currentSessionIndex === sessions.length - 1}
                onClick={() => this.goToSession(sessions.length - 1)}
              >
                Last
              </button>
            </div>

            <div className="session-details">
              <p>
                <strong>Session Start:</strong> {this.formatDate(sessions[currentSessionIndex].startTime)}
                {' | '}
                <strong>Session End:</strong> {this.formatDate(sessions[currentSessionIndex].endTime)}
                {' | '}
                <strong>Events:</strong> {sessions[currentSessionIndex].events.length}
              </p>
            </div>

            <div className="logs-table-container">
              <table className="stats-table logs-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions[currentSessionIndex].events.map((log, index) => (
                    <tr key={index} className={this.getEventTypeClass(log.eventType)}>
                      <td className="log-time">{this.formatDate(log.timestamp)}</td>
                      <td className="log-event">{log.eventType}</td>
                      <td className="log-details">
                        {this.renderLogDetails(log)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  renderLogDetails(log) {
    const { data } = log;
    if (!data) return '-';

    switch (log.eventType) {
      case 'CONNECTION':
      case 'DISCONNECTION':
        return `${data.name || 'unknown'} (${data.socketId || '-'})`;
      case 'COUPLE_START':
        return `${data.name1 || 'unknown'} + ${data.name2 || 'unknown'}`;
      case 'COUPLE_END':
        return `${data.name1 || 'unknown'} + ${data.name2 || 'unknown'} (${data.reason || 'unknown'})`;
      default:
        return JSON.stringify(data);
    }
  }

  render() {
    const { status } = this.state;
    const { roomId } = this.props;

    if (status === 'error') {
      return this.renderError();
    }

    if (status === 'connecting') {
      return this.renderLoading();
    }

    return (
      <div className="stats-dashboard" ref={this.dashboardRef}>
        <header className="stats-header">
          <h1>Stats Dashboard</h1>
          <span className="room-badge">Room: {roomId}</span>
        </header>

        {this.renderSummaryCards()}

        <div className="stats-grid">
          <div className="stats-column">
            {this.renderConnectedUsersTable()}
            {this.renderActiveCouplesTable()}
          </div>
          <div className="stats-column">
            {this.renderOrphanUsersTable()}
            {this.renderSessionInfo()}
          </div>
        </div>

        {this.renderLogsSection()}
      </div>
    );
  }
}

export default StatsDashboard;
